<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import SelectInput from '@/components/common/selects/SelectInput.vue'
import ConfirmModal from '@/components/common/layout/ConfirmModal.vue'
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import {
  useFeatureFlagAdminStore,
  type FeatureFlagItem,
  type FeatureFlagDetail,
  type FeatureFlagStatus,
} from '@/stores/useFeatureFlagAdminStore'
import {
  PlusIcon,
  TrashIcon,
  FlagIcon,
} from '@heroicons/vue/24/outline'

const store = useFeatureFlagAdminStore()

const showCreateModal = ref(false)
const showDetailModal = ref(false)
const showDeleteModal = ref(false)

const formCode = ref('')
const formDescription = ref('')
const formStatus = ref<FeatureFlagStatus>('ACTIVE')
const saving = ref(false)
const errorMsg = ref<string | null>(null)

const detail = ref<FeatureFlagDetail | null>(null)
const isLoadingDetail = ref(false)
const editingDescription = ref('')
const editingStatus = ref<FeatureFlagStatus>('ACTIVE')
const updating = ref(false)

const flagToDelete = ref<FeatureFlagItem | null>(null)
const deleteError = ref<string | null>(null)

const STATUS_OPTIONS: { id: string; value: FeatureFlagStatus; label: string }[] = [
  { id: 'ACTIVE', value: 'ACTIVE', label: 'Actif' },
  { id: 'INACTIVE', value: 'INACTIVE', label: 'Inactif' },
  { id: 'DEPRECATED', value: 'DEPRECATED', label: 'Déprécié' },
]

const flags = computed(() => store.flags)

const gridColumns = computed(() => [
  {
    field: 'code',
    headerName: 'Code',
    filter: false,
  },
  {
    field: 'description',
    headerName: 'Description',
    valueFormatter: (params: any) => params.value || '—',
    filter: false,
  },
  {
    field: 'status',
    headerName: 'Statut',
    cellClass: (params: any) =>
      params.value === 'ACTIVE'
        ? 'text-success'
        : params.value === 'DEPRECATED'
          ? 'text-error'
          : 'text-base-content/60',
    filter: false,
    minWidth: 110,
    maxWidth: 130,
  },
  {
    field: 'userCount',
    headerName: 'Utilisateurs',
    filter: false,
    minWidth: 110,
    maxWidth: 140,
  },
  {
    field: 'dateCreated',
    headerName: 'Créé le',
    valueFormatter: (params: any) => formatDate(params.value),
    filter: false,
    minWidth: 140,
    maxWidth: 180,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    resizable: false,
    sortable: false,
    filter: false,
    minWidth: 110,
    maxWidth: 110,
    cellRenderer: (params: any) => {
      const wrapper = document.createElement('div')
      wrapper.className = 'flex gap-1'

      const eye = document.createElement('button')
      eye.className = 'btn btn-ghost btn-xs'
      eye.title = 'Voir le détail'
      eye.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>'
      eye.onclick = () => openDetail(params.data)

      const trash = document.createElement('button')
      trash.className = 'btn btn-ghost btn-xs text-error'
      trash.title = 'Supprimer'
      trash.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>'
      trash.onclick = () => askDelete(params.data)

      wrapper.appendChild(eye)
      wrapper.appendChild(trash)
      return wrapper
    },
    cellClass: 'border-0 grid-action',
  },
])

function formatDate(s: string | null | undefined) {
  if (!s) return '—'
  return new Date(s).toLocaleString('fr-FR')
}

function openCreate() {
  formCode.value = ''
  formDescription.value = ''
  formStatus.value = 'ACTIVE'
  errorMsg.value = null
  showCreateModal.value = true
}

function closeCreate() {
  showCreateModal.value = false
  errorMsg.value = null
}

async function submitCreate() {
  if (!formCode.value.trim()) {
    errorMsg.value = 'Le code est obligatoire'
    return
  }
  saving.value = true
  errorMsg.value = null
  try {
    const res = await store.createFlag({
      code: formCode.value.trim().toUpperCase(),
      description: formDescription.value.trim() || null,
      status: formStatus.value,
    })
    if (res.ok) {
      closeCreate()
    } else {
      errorMsg.value = res.message
    }
  } finally {
    saving.value = false
  }
}

async function openDetail(flag: FeatureFlagItem) {
  detail.value = null
  isLoadingDetail.value = true
  showDetailModal.value = true
  const fetched = await store.fetchFlagDetail(flag.uuid)
  if (fetched) {
    detail.value = fetched
    editingDescription.value = fetched.description ?? ''
    editingStatus.value = fetched.status
  }
  isLoadingDetail.value = false
}

function closeDetail() {
  showDetailModal.value = false
  detail.value = null
}

async function saveDetail() {
  if (!detail.value) return
  updating.value = true
  try {
    const ok = await store.updateFlag(detail.value.uuid, {
      description: editingDescription.value.trim() || null,
      status: editingStatus.value,
    })
    if (ok) {
      // Refresh detail with new values
      const fresh = await store.fetchFlagDetail(detail.value.uuid)
      if (fresh) detail.value = fresh
    }
  } finally {
    updating.value = false
  }
}

async function unassignUser(userUuid: string) {
  if (!detail.value) return
  const ok = await store.unassignFlagFromUser(detail.value.uuid, userUuid)
  if (ok) {
    detail.value.users = detail.value.users.filter((u) => u.userUuid !== userUuid)
    detail.value.userCount = detail.value.users.length
    detail.value.assignmentsCount = Math.max(0, detail.value.assignmentsCount - 1)
    // Sync grid count
    const idx = store.flags.findIndex((f) => f.uuid === detail.value!.uuid)
    if (idx >= 0) {
      store.flags[idx].userCount = detail.value.userCount
      store.flags[idx].assignmentsCount = detail.value.assignmentsCount
    }
  }
}

function askDelete(flag: FeatureFlagItem) {
  flagToDelete.value = flag
  deleteError.value = null
  showDeleteModal.value = true
}

async function confirmDelete() {
  if (!flagToDelete.value) return
  const res = await store.deleteFlag(flagToDelete.value.uuid)
  if (res.ok) {
    showDeleteModal.value = false
    flagToDelete.value = null
  } else {
    deleteError.value = res.message
  }
}

onMounted(async () => {
  await store.fetchFlags()
})
</script>

<template>
  <div class="p-6">
    <Bs2Title title="Feature flags" subtitle="SUPER_ADMIN uniquement" />

    <div class="flex flex-wrap gap-2 mt-4">
      <router-link to="/admin/users" class="btn btn-outline btn-sm">
        Utilisateurs
      </router-link>
    </div>

    <div class="mt-8 card bg-base-100 shadow">
      <div class="card-body">
        <GridVue
          :list="flags"
          :cols="gridColumns"
          :use-server-side-pagination="false"
        >
          <template #title>
            <h2 class="card-title flex items-center gap-2">
              <FlagIcon class="w-5 h-5" />
              Feature flags ({{ flags.length }})
            </h2>
          </template>
          <template #filter>
            <div class="flex flex-row items-end gap-2 flex-nowrap">
              <ButtonDaisy
                text="Actualiser"
                variant="outline"
                size="sm"
                @click="store.fetchFlags()"
              />
              <button class="btn btn-primary btn-sm gap-1" @click="openCreate">
                <PlusIcon class="w-4 h-4" />
                Créer un flag
              </button>
            </div>
          </template>
        </GridVue>
      </div>
    </div>

    <!-- Modal création -->
    <div class="modal" :class="{ 'modal-open': showCreateModal }">
      <div class="modal-box max-w-lg">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <PlusIcon class="w-6 h-6" />
          Créer un feature flag
        </h3>

        <div class="mt-4 space-y-4">
          <div class="form-control">
            <label class="label"><span class="label-text">Code (MAJUSCULES_AVEC_UNDERSCORES)</span></label>
            <input
              v-model="formCode"
              type="text"
              placeholder="NEW_DASHBOARD_2026"
              class="input input-bordered input-sm font-mono"
              :disabled="saving"
              @input="formCode = formCode.toUpperCase().replace(/[^A-Z0-9_]/g, '_')"
            />
          </div>
          <div class="form-control">
            <label class="label"><span class="label-text">Description</span></label>
            <textarea
              v-model="formDescription"
              class="textarea textarea-bordered textarea-sm"
              rows="2"
              placeholder="Optionnel : à quoi sert ce flag ?"
              :disabled="saving"
            />
          </div>
          <SelectInput
            v-model="formStatus"
            label="Statut"
            :options="STATUS_OPTIONS"
          />

          <div v-if="errorMsg" class="alert alert-error text-sm">{{ errorMsg }}</div>
        </div>

        <div class="modal-action">
          <button class="btn" :disabled="saving" @click="closeCreate">Annuler</button>
          <button
            class="btn btn-primary"
            :disabled="saving || !formCode.trim()"
            @click="submitCreate"
          >
            {{ saving ? 'Création…' : 'Créer' }}
          </button>
        </div>
      </div>
      <div class="modal-backdrop" @click="closeCreate" />
    </div>

    <!-- Modal détail / édition -->
    <div class="modal" :class="{ 'modal-open': showDetailModal }">
      <div class="modal-box max-w-3xl">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <FlagIcon class="w-6 h-6" />
          {{ detail?.code ?? '…' }}
        </h3>

        <div v-if="isLoadingDetail" class="py-8 text-center">
          <span class="loading loading-spinner loading-lg" />
        </div>

        <div v-else-if="detail" class="mt-4 space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="label"><span class="label-text">Description</span></label>
              <textarea
                v-model="editingDescription"
                class="textarea textarea-bordered textarea-sm w-full"
                rows="2"
                :disabled="updating"
              />
            </div>
            <SelectInput
              v-model="editingStatus"
              label="Statut"
              :options="STATUS_OPTIONS"
            />
          </div>

          <div class="grid grid-cols-3 gap-3 text-sm">
            <div>
              <span class="text-base-content/60">Utilisateurs :</span>
              <span class="ml-1 font-medium">{{ detail.userCount }}</span>
            </div>
            <div>
              <span class="text-base-content/60">Affectations totales :</span>
              <span class="ml-1 font-medium">{{ detail.assignmentsCount }}</span>
            </div>
            <div>
              <span class="text-base-content/60">Créé le :</span>
              <span class="ml-1 font-medium">{{ formatDate(detail.dateCreated) }}</span>
            </div>
          </div>

          <div class="border-t pt-4">
            <h4 class="font-medium text-sm mb-2">
              Utilisateurs avec ce flag ({{ detail.users.length }})
            </h4>
            <div v-if="detail.users.length === 0" class="text-base-content/70">
              Aucun utilisateur n'a encore ce flag.
            </div>
            <div v-else class="overflow-x-auto max-h-72">
              <table class="table table-xs">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Nom</th>
                    <th>Affecté le</th>
                    <th>Affecté par</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="u in detail.users" :key="u.assignmentUuid">
                    <td>{{ u.email }}</td>
                    <td>{{ [u.firstName, u.lastName].filter(Boolean).join(' ') || '—' }}</td>
                    <td>{{ formatDate(u.grantedAt) }}</td>
                    <td>{{ u.grantedByEmail ?? '—' }}</td>
                    <td>
                      <button
                        class="btn btn-ghost btn-xs text-error"
                        title="Retirer ce flag"
                        @click="unassignUser(u.userUuid)"
                      >
                        <TrashIcon class="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="modal-action">
          <button class="btn" @click="closeDetail">Fermer</button>
          <button
            v-if="detail"
            class="btn btn-primary"
            :disabled="updating"
            @click="saveDetail"
          >
            {{ updating ? 'Enregistrement…' : 'Enregistrer' }}
          </button>
        </div>
      </div>
      <div class="modal-backdrop" @click="closeDetail" />
    </div>

    <ConfirmModal
      :show-modal="showDeleteModal"
      title="Supprimer le feature flag"
      :body="`Êtes-vous sûr de vouloir supprimer « ${flagToDelete?.code ?? ''} » ? Cette action est irréversible.`"
      confirm-text="Supprimer"
      confirm-variant="error"
      @confirm="confirmDelete"
      @cancel="showDeleteModal = false; flagToDelete = null; deleteError = null"
    />
    <div v-if="deleteError" class="toast toast-end">
      <div class="alert alert-error text-sm">{{ deleteError }}</div>
    </div>
  </div>
</template>
